import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import StageBadge from '../../../components/StageBadge';
import { useCurrency } from '../../../contexts/CurrencyContext';

// ✅ Source de vérité
import {
  calculateDealScorePercent,
  getAiRecommendationBadge,
  calculateOverviewKPIs
} from '../../../utils/propertyKpis';

/**
 * KPI Card style "landing" (petites cartes blanches avec icône + label + valeur)
 */
const KPIChip = ({ icon = 'BarChart3', label, value, tone = 'neutral' }) => {
  const valueClass =
    tone === 'good'
      ? 'text-emerald-600'
      : tone === 'bad'
      ? 'text-red-600'
      : 'text-gray-900';

  return (
    <div className="rounded-2xl bg-white border border-gray-100 shadow-sm px-4 py-3">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 h-8 w-8 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
          <Icon name={icon} size={16} className="text-gray-500" />
        </div>

        <div className="min-w-0">
          <div className="text-[12px] font-medium text-gray-500 leading-tight">
            {label}
          </div>
          <div className={`mt-1 text-[16px] font-semibold leading-tight ${valueClass}`}>
            {value}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper: détecter URL complète
const isHttpUrl = (v) => typeof v === 'string' && /^https?:\/\//i.test(v);

const PropertyCard = ({ property, onClick, onStageChange, onDelete, userProfile }) => {
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();

  const handleCardClick = () => {
    if (onClick) return onClick();
    navigate(`/analysis-results?propertyId=${property?.id}`);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete?.(property?.id);
  };

  // ✅ KPIs calculés une seule fois (identiques à analysis-results)
  const kpis = useMemo(() => calculateOverviewKPIs(property), [property]);

  // ✅ Score: priorité aux scores stockés dans la row (si existants), sinon calcul
  const score = useMemo(() => {
    const stored = property?.deal_score ?? property?.aiAnalysis?.score ?? property?.score ?? null;
    if (stored != null) return Math.round(Number(stored));

    return calculateDealScorePercent({ ...property, kpis }, userProfile);
  }, [property, kpis, userProfile]);

  // ✅ Couleurs inline safe pour le badge Score
  const scoreStyle = useMemo(() => {
    const n = Number(score);
    if (!Number.isFinite(n)) return { background: '#64748b', color: '#fff' }; // slate

    if (n >= 80) return { background: '#16a34a', color: '#fff' }; // green
    if (n >= 60) return { background: '#22c55e', color: '#fff' }; // light green
    if (n >= 40) return { background: '#eab308', color: '#111827' }; // yellow
    if (n >= 20) return { background: '#f97316', color: '#111827' }; // orange
    return { background: '#ef4444', color: '#fff' }; // red
  }, [score]);

  const aiBadge = useMemo(() => getAiRecommendationBadge(property), [property]);

  const plexLabel = property?.plex ? `${property.plex}-plex` : null;
  const units = property?.units ?? property?.num_units ?? null;
  const price = property?.purchase_price ?? property?.price ?? null;

  /**
   * ✅ FIX STORAGE:
   * - Si c'est une URL complète -> on l'utilise telle quelle (pas de signed URL)
   * - Sinon, on tente un filePath (supabase storage path)
   */
  const resolvedImageSrc = useMemo(() => {
    // priorité: imageUrl explicite si fourni
    if (isHttpUrl(property?.imageUrl)) return property.imageUrl;

    // ensuite: property.image si c'est déjà une URL
    if (isHttpUrl(property?.image)) return property.image;

    // ensuite: filePath explicite (recommandé)
    if (property?.imageFilePath) return property.imageFilePath;

    // ensuite: structure photos [{ url, filePath }]
    const p0 = property?.photos?.[0];
    if (p0?.filePath) return p0.filePath;
    if (isHttpUrl(p0?.url)) return p0.url;

    return null;
  }, [property]);

  return (
    <div
      onClick={handleCardClick}
      className="relative overflow-hidden rounded-3xl border border-border bg-card shadow-sm cursor-pointer"
    >
      {/* Image background */}
      <div className="relative h-[230px] w-full bg-muted">
        {resolvedImageSrc ? (
          <Image
            src={resolvedImageSrc}
            alt={property?.imageAlt || property?.address}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-muted-foreground">
            <Icon name="ImageOff" size={22} />
          </div>
        )}

        {/* Top-right: stage + IA badge */}
        <div
          className="absolute top-3 right-3 z-10 flex flex-col items-end gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <StageBadge
            stage={property?.stage || property?.status}
            onStageChange={onStageChange}
            propertyId={property?.id}
            variant="card"
          />

          {aiBadge && (
            <div className="overflow-hidden rounded-full shadow-sm" style={{ background: aiBadge.bg }}>
              <div className="flex items-center">
                <div
                  className="px-3 py-1 text-[11px] font-bold text-white"
                  style={{ background: 'rgba(0,0,0,0.18)' }}
                >
                  IA
                </div>
                <div className="px-3 py-1 text-[11px] font-bold text-white tracking-wide">
                  {aiBadge.label}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* White overlay pill */}
        <div className="absolute left-3 right-3 bottom-3 z-10">
          <div className="rounded-2xl bg-white/95 backdrop-blur border border-gray-100 shadow-sm px-4 py-3 flex items-center justify-between gap-3">
            {/* Left */}
            <div className="min-w-0">
              <div className="text-[16px] font-semibold text-gray-900 truncate">
                {property?.address}
              </div>

              <div className="mt-1 flex items-center gap-2 text-sm text-gray-500">
                {plexLabel && <span>{plexLabel}</span>}
                {units != null && (
                  <>
                    <span className="opacity-40">•</span>
                    <span>{units} unités</span>
                  </>
                )}
                {price != null && (
                  <>
                    <span className="opacity-40">•</span>
                    <span className="font-medium text-gray-700">{formatCurrency(price)}</span>
                  </>
                )}
              </div>
            </div>

            {/* Right: score */}
            {score != null && (
              <div
                className="shrink-0 rounded-2xl px-4 py-2 text-center leading-tight"
                style={scoreStyle}
              >
                <div className="text-xs font-medium opacity-90">Score</div>
                <div className="text-lg font-bold">{score}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* KPI cards (landing style) */}
      <div className="px-4 pt-6 pb-4">

        <div className="grid grid-cols-2 gap-3">
          <KPIChip
            icon="TrendingUp"
            label="Cashflow mensuel"
            value={formatCurrency(kpis?.monthlyCashflow ?? 0)}
            tone={(kpis?.monthlyCashflow ?? 0) >= 0 ? 'good' : 'bad'}
          />

          <div className="rounded-2xl bg-white border border-gray-100 shadow-sm px-4 py-3">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 h-8 w-8 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
                <Icon name="Percent" size={16} className="text-gray-500" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[12px] font-medium text-gray-500 leading-tight mb-1.5">Rendement</div>
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[11px] text-gray-400">Cash-on-Cash</span>
                    <span className={`text-[14px] font-bold ${(kpis?.cashOnCashReturn ?? 0) >= 5 ? 'text-emerald-600' : 'text-gray-900'}`}>
                      {(kpis?.cashOnCashReturn ?? 0).toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[11px] text-gray-400">ROI moy. / an</span>
                    <span className={`text-[14px] font-bold ${(kpis?.avgAnnualROI ?? 0) >= 5 ? 'text-emerald-600' : 'text-gray-900'}`}>
                      {(kpis?.avgAnnualROI ?? 0).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <KPIChip
            icon="BarChart3"
            label="Taux de capitalisation"
            value={`${(kpis?.capRate ?? 0).toFixed(1)}%`}
            tone={(kpis?.capRate ?? 0) >= 4 ? 'good' : 'neutral'}
          />

          <KPIChip
            icon="Shield"
            label="Ratio couverture dette"
            value={`${(kpis?.dscr ?? 0).toFixed(2)}x`}
            tone={(kpis?.dscr ?? 0) >= 1.25 ? 'good' : (kpis?.dscr ?? 0) >= 1 ? 'neutral' : 'bad'}
          />
        </div>
      </div>

      {/* Bottom line */}
      <div className="px-4 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
          <Icon name="MapPin" size={14} />
          <span className="truncate">
            {property?.city}
            {property?.province ? `, ${property.province}` : ''}
          </span>
        </div>

        {onDelete && (
          <button
            className="h-9 w-9 rounded-xl border border-border bg-card flex items-center justify-center"
            onClick={handleDelete}
            aria-label="Supprimer"
          >
            <Icon name="Trash2" size={16} color="var(--color-destructive)" />
          </button>
        )}
      </div>
    </div>
  );
};

export default PropertyCard;
